import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, MessageSquare, Video } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import { TASK_STATUS, TASK_TYPE_LABELS, type TaskType, type TaskStatus } from '@parecer/shared';
import { formatBRL, formatRelativeDate } from '@/lib/utils';

interface TaskItem {
  taskId: string;
  ownerId: string;
  lawyerId?: string;
  type: TaskType;
  status: TaskStatus;
  description: string;
  totalAmount: number;
  deadlineDays?: number;
  createdAt: string;
}

/**
 * User-facing tasks list.
 *
 * The backend's listTasks for users defaults to open status because there's no
 * GSI on owner. We query each meaningful status in parallel and merge — fine at
 * MVP scale (a single user has dozens of tasks max). When that grows, add a
 * dedicated GSI4 keyed on USER#<ownerId> and replace this with one query.
 */
export function UserTarefas() {
  const statuses = [
    TASK_STATUS.OPEN,
    TASK_STATUS.ASSIGNED,
    TASK_STATUS.IN_PROGRESS,
    TASK_STATUS.DELIVERED,
    TASK_STATUS.COMPLETED,
  ];

  const queries = useQuery({
    queryKey: ['user', 'tasks', 'all'],
    queryFn: async () => {
      const results = await Promise.all(
        statuses.map((s) => api.get<{ items: TaskItem[] }>(`/tasks?status=${s}`)),
      );
      const seen = new Set<string>();
      const flat: TaskItem[] = [];
      for (const r of results) {
        for (const t of r.items) {
          if (!seen.has(t.taskId)) {
            seen.add(t.taskId);
            flat.push(t);
          }
        }
      }
      return flat.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    },
  });

  const items = queries.data ?? [];

  return (
    <AppShell role="users">
      <div className="max-w-5xl">
        <h1 className="font-serif text-display-lg">Tarefas</h1>
        <p className="text-muted-foreground mt-1">Suas contratações ativas e histórico.</p>

        <div className="mt-8 space-y-3">
          {queries.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
          {!queries.isLoading && items.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                Você ainda não tem tarefas. Suba um contrato e contrate um advogado para começar.
              </CardContent>
            </Card>
          )}
          {items.map((t) => (
            <Link key={t.taskId} to={`/app/tarefas/${t.taskId}`} className="block focus-ring">
              <Card className="hover:border-accent/40 transition-colors">
                <CardContent className="py-4 flex items-center gap-4">
                  <ClipboardList className="h-5 w-5 text-muted-foreground flex-none" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{TASK_TYPE_LABELS[t.type]}</h3>
                      <StatusBadge status={t.status} />
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-1">{t.description}</p>
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Criada {formatRelativeDate(t.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex-none text-right">
                    {t.totalAmount > 0 && (
                      <p className="font-serif text-lg">{formatBRL(t.totalAmount)}</p>
                    )}
                    <div className="mt-2 flex justify-end gap-1 text-muted-foreground">
                      <MessageSquare className="h-4 w-4" />
                      <Video className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: 'success' | 'warning' | 'danger' | 'muted'; label: string }> = {
    open: { variant: 'warning', label: 'Aberta' },
    assigned: { variant: 'warning', label: 'Atribuída' },
    in_progress: { variant: 'warning', label: 'Em andamento' },
    delivered: { variant: 'success', label: 'Entregue' },
    completed: { variant: 'success', label: 'Concluída' },
    rejected: { variant: 'muted', label: 'Rejeitada' },
    cancelled: { variant: 'muted', label: 'Cancelada' },
    disputed: { variant: 'danger', label: 'Em disputa' },
  };
  const info = map[status] ?? { variant: 'muted' as const, label: status };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}
